import Data from "./Data.js"
import { Entry } from "./Data.js"

export default class Display {
	table: HTMLTableElement
	sortBy: string
	primaryKey: string
	descending: boolean
	showImages: boolean
	displayCount: number
	hideOffline: boolean
	currentPage: number
	
	data?: Data
	headerElem?: HTMLTableRowElement
	rows?: HTMLTableRowElement[]
	
	maxPage?: number
	selectElem?: HTMLSelectElement
	prevButton?: HTMLButtonElement
	nextButton?: HTMLButtonElement
	
	getImageURL?: (key: any, entry: Entry) => string
	addValue?: (td: HTMLTableCellElement, entry: Entry, entryName: string, column: string) => void
	updateValue?: (td: HTMLTableCellElement, entry: Entry, entryName: string, column: string, value: any) => void
	
	constructor({table, sortBy, primaryKey, descending = false, showImages = true, displayCount = 0}){
		this.table = table
		this.sortBy = sortBy
		this.primaryKey = primaryKey
		this.descending = descending
		this.showImages = showImages
		this.displayCount = displayCount
		this.hideOffline = false
		this.currentPage = 1
	}
	
	init(data: Data){
		this.data = data
		
		// Set pagination controls
		if(this.displayCount > 0){
			this.initPagination()
		}else{
			// Hide pagination controls when pagination is disabled
			const paginationSpanElem: HTMLSpanElement = document.querySelector("span.webstats-pagination")
			if(paginationSpanElem) paginationSpanElem.style.display = "none"
		}
		
		// Clear table
		this.table.innerHTML = ""
		
		// Create header of columns
		this.headerElem = document.createElement("tr")
		this.table.append(this.headerElem)
		for(const column of this.data.columns){
			Display.appendTh(this.headerElem, column, this.thClick.bind(this), (this.showImages && column == this.primaryKey) ? 2 : undefined)
		}
		
		// Create rows of (empty) entries
		this.rows = []
		for(const entry of this.data.entries){
			this.appendEntry(entry)
		}
		
		// Fill entries
		this.updateData()
	}
	
	initPagination(){
		this.maxPage = Math.ceil(this.data.entries.length / this.displayCount)
		
		// Page selector
		this.selectElem = document.querySelector("select.webstats-pagination")
		if(this.selectElem) this.selectElem.onchange = (e) =>
			this.changePage(Number((e.target as HTMLSelectElement).value))
		else console.warn("WebStats: no/invalid page control elements")
		
		// "Prev" button
		this.prevButton = document.querySelector("button.webstats-pagination[name=prev]")
		if(this.prevButton) this.prevButton.onclick = () => this.changePage(this.currentPage-1)
		else console.warn("WebStats: no/invalid page control elements")
		
		// "Next" button
		this.nextButton = document.querySelector("button.webstats-pagination[name=next]")
		if(this.nextButton) this.nextButton.onclick = () => this.changePage(this.currentPage+1)
		else console.warn("WebStats: no/invalid page control elements")
		
		this.updatePagination()
	}
	
	updatePagination(){
		this.maxPage = Math.ceil(this.data.entries.length / this.displayCount)
		
		// Page selector
		if(this.selectElem){
			this.selectElem.innerHTML = ""
			for(let i = 1; i <= this.maxPage; i++){
				const optionElem = document.createElement("option")
				optionElem.innerText = String(i)
				this.selectElem.append(optionElem)
			}
			this.selectElem.value = String(this.currentPage)
		}
		
		// "Prev" button
		if(this.prevButton) this.prevButton.toggleAttribute("disabled", this.currentPage <= 1)
		
		// "Next" button
		if(this.nextButton) this.nextButton.toggleAttribute("disabled", this.currentPage >= this.maxPage)
	}
	
	appendEntry(entry: Entry){
		const entryName = entry[this.primaryKey].toString()
		let tr = document.createElement("tr")
		
		// Append empty elements for alignment
		for(const column of this.data.columns){
			// Append image
			if(this.showImages && column == this.primaryKey){
				let img = Display.appendElement(tr, "td")
				Display.appendImg(img, "")
				img.classList.add("sticky")
				img.setAttribute("title", entryName)
			}
			
			let td = Display.appendElement(tr, "td") as HTMLTableCellElement
			td.classList.add("empty")
			td.setAttribute("column", Display.quoteEscape(column))
			if(this.addValue != undefined) this.addValue(td, entry, entryName, column)
		}
		this.rows.push(tr)
	}
	
	setImage(entry: Entry){
		const row = this.rows[entry[Data.idxSymbol]]
		const img = row.getElementsByTagName("img")[0]
		if(img) img.src = this.getImageURL(entry[this.primaryKey], entry)
	}
	
	updateData(){
		for(const entry of this.data.entries){
			const rowElem = this.rows[entry[Data.idxSymbol]]
			for(const column in entry){
				const value = entry[column]
				const td = rowElem.querySelector(`td[column='${column}']`) as HTMLTableCellElement
				if (!td) continue
				td.classList.remove("empty")
				td.setAttribute("value", value)
				if(this.updateValue != undefined){
					this.updateValue(td, entry, entry[this.primaryKey].toString(), column, value)
				}else{
					td.innerText = isNaN(value) ? value : Number(value).toLocaleString()
				}
			}
		}
	}
	
	// Change the page, re-display if `show` is not false and set page controls
	changePage(page: number, show?: boolean){
		page = Math.max(1, Math.min(page, this.maxPage))
		this.currentPage = page
		if(show != false) this.show()
		
		if(this.displayCount > 0) this.updatePagination()
	}
	
	// Re-display table contents
	show(){
		this.table.innerHTML = ""
		this.table.append(this.headerElem)
		const entries = this.data.entries
		const min = (this.currentPage-1) * this.displayCount
		const max = (this.displayCount > 0)
			? Math.min(this.currentPage * this.displayCount, entries.length)
			: entries.length
		
		for(let i = min; i < max; i++){
			const entry = entries[i]
			if(this.showImages) this.setImage(entry)
			this.table.append(this.rows[entry[Data.idxSymbol]])
		}
	}
	
	// Sort a HTML table element
	sort(by = this.sortBy, descending = this.descending){
		this.data.sort(by, descending)
		this.show()
	}
	
	// When a table header is clicked, sort by that header
	thClick(e: Event){
		let column = (e.target as HTMLElement).innerText.toLowerCase()
		this.descending = (column === this.sortBy) ? !this.descending : true
		this.sortBy = column
		if(this.displayCount > 0) this.changePage(1, false)
		this.sort()
		
		// Set URL query string, for sharing
		window.history.replaceState({}, "",
			location.pathname + "?sort=" + this.sortBy.replace(/\s/g, "+")
			+ "&order=" + (this.descending ? "desc" : "asc"))
	}
	
	// Replace single quotes by '&quot;' (html-escape)
	static quoteEscape = (string: string) => string.replace(/'/g, "&quot;")
	
	static appendElement(base: HTMLElement, type: string){
		let el = document.createElement(type)
		base.append(el)
		return el
	}
	
	static prependElement(base: HTMLElement, type: string){
		let el = document.createElement(type)
		base.prepend(el)
		return el
	}
	
	static appendTextElement(base: HTMLElement, type: string, name: string){
		let el = Display.appendElement(base, type)
		el.innerText = name
		return el
	}
	
	static appendTh(base: HTMLElement, name: string, onclick: (this: GlobalEventHandlers, ev: MouseEvent) => any, colspan?: number){
		let th = Display.appendTextElement(base, "th", name)
		th.onclick = onclick
		if(colspan != undefined) th.setAttribute("colspan", String(colspan))
		return th
	}
	
	static appendImg(base: HTMLElement, src: string){
		let img = Display.appendElement(base, "img") as HTMLIFrameElement
		img.src = src
		return img
	}
	
}
